import type { DurableChatTurnIdentity, DurableChatTurnSubscriptionMessage } from "@/api/models"

import { BACKEND_URI } from "@/constants/config"

import { computeBackoffDelayMs } from "./durable-chat-turn-registry-utils"

export type DurableChatTurnConnectionState = "connecting" | "connected" | "reconnecting" | "stopped"

export type DurableChatTurnSocket = {
  onclose: ((event: { code: number; reason: string }) => void) | null
  onerror: (() => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
  onopen: (() => void) | null
  close: (code?: number, reason?: string) => void
  send: (data: string) => void
}

export type DurableChatTurnSubscriptionOptions = DurableChatTurnIdentity & {
  getAfterSeq: () => number
  getToken: () => string | undefined
  onConnectionStateChange?: (state: DurableChatTurnConnectionState) => void
  onError?: (error: Error) => void
  onMessage: (message: DurableChatTurnSubscriptionMessage) => void
  onReconnectExhausted?: () => Promise<void> | void
}

type DurableChatTurnSubscriptionDependencies = {
  backendUri?: string
  baseReconnectDelayMs?: number
  createSocket?: (url: string) => DurableChatTurnSocket
  maxReconnectAttempts?: number
  maxReconnectDelayMs?: number
}

const DEFAULT_BASE_RECONNECT_DELAY_MS = 500
const DEFAULT_MAX_RECONNECT_DELAY_MS = 10_000
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 8

function defaultCreateSocket(url: string): DurableChatTurnSocket {
  return new WebSocket(url) as unknown as DurableChatTurnSocket
}

function websocketUrl(
  backendUri: string,
  { sessionId, turnId }: DurableChatTurnIdentity,
  afterSeq: number
): string {
  const fallbackOrigin = typeof window === "undefined" ? "http://localhost" : window.location.origin
  const url = new URL(backendUri, fallbackOrigin)
  const basePath = url.pathname.replace(/\/$/, "")
  url.pathname = `${basePath}/ws/chat-turns/${encodeURIComponent(sessionId)}/${encodeURIComponent(turnId)}`
  url.search = ""
  url.searchParams.set("afterSeq", String(afterSeq))
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:"
  return url.toString()
}

export class DurableChatTurnSubscription {
  private readonly backendUri: string
  private readonly baseReconnectDelayMs: number
  private readonly createSocket: (url: string) => DurableChatTurnSocket
  private readonly maxReconnectAttempts: number
  private readonly maxReconnectDelayMs: number
  private readonly options: DurableChatTurnSubscriptionOptions
  private reconnectAttempts = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private socket: DurableChatTurnSocket | null = null
  private started = false
  private stopped = false

  constructor(
    options: DurableChatTurnSubscriptionOptions,
    dependencies: DurableChatTurnSubscriptionDependencies = {}
  ) {
    this.options = options
    this.backendUri = dependencies.backendUri ?? BACKEND_URI
    this.baseReconnectDelayMs = dependencies.baseReconnectDelayMs ?? DEFAULT_BASE_RECONNECT_DELAY_MS
    this.createSocket = dependencies.createSocket ?? defaultCreateSocket
    this.maxReconnectAttempts = dependencies.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS
    this.maxReconnectDelayMs = dependencies.maxReconnectDelayMs ?? DEFAULT_MAX_RECONNECT_DELAY_MS
  }

  start(): void {
    if (this.started && !this.stopped) return
    this.started = true
    this.stopped = false
    // A restart after giving up (registry `resync()`) gets a fresh budget.
    this.reconnectAttempts = 0
    this.connect("connecting")
  }

  stop(): void {
    if (this.stopped) return
    this.stopped = true
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    const socket = this.socket
    this.socket = null
    socket?.close(1000, "Durable turn subscription stopped")
    this.options.onConnectionStateChange?.("stopped")
  }

  /** Skip the remaining reconnect backoff — the tab became visible or the
   *  browser came back online, so the delay chosen while hidden/offline no
   *  longer applies (issue #3354). No-op unless a reconnect is pending: a live
   *  or connecting socket is left alone, and a stopped subscription is the
   *  registry's to restart. */
  reconnectNow(): void {
    if (this.stopped || this.reconnectTimer === null) return
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.reconnectAttempts = 0
    this.connect("reconnecting")
  }

  private connect(connectionState: "connecting" | "reconnecting"): void {
    if (this.stopped) return
    this.options.onConnectionStateChange?.(connectionState)

    const target: DurableChatTurnIdentity = {
      sessionId: this.options.sessionId,
      turnId: this.options.turnId,
      epoch: this.options.epoch,
    }
    const socket = this.createSocket(
      websocketUrl(this.backendUri, target, this.options.getAfterSeq())
    )
    this.socket = socket

    socket.onopen = () => {
      if (this.socket !== socket || this.stopped) return
      const idToken = this.options.getToken()
      if (!idToken) {
        const error = new Error("Missing authentication token")
        this.stopped = true
        this.options.onError?.(error)
        socket.close(4001, error.message)
        this.options.onConnectionStateChange?.("stopped")
        // A missing token means this subscription instance will never
        // reconnect — but the turn itself may still be running. Without this,
        // the registry entry never leaves its non-terminal state and the UI
        // stays stuck (issue #2513 finding 5). `onclose` below cannot carry
        // this signal instead: it returns early once `this.stopped` is true.
        this.notifyReconnectExhausted()
        return
      }
      socket.send(JSON.stringify({ idToken }))
      this.options.onConnectionStateChange?.("connected")
    }

    socket.onmessage = (event) => {
      if (this.socket !== socket || this.stopped) return
      try {
        if (typeof event.data !== "string") {
          throw new Error("Durable turn subscription received a non-text frame")
        }
        const message = JSON.parse(event.data) as DurableChatTurnSubscriptionMessage
        this.reconnectAttempts = 0
        this.options.onMessage(message)
      } catch (error) {
        this.options.onError?.(
          error instanceof Error ? error : new Error("Invalid durable turn subscription frame")
        )
      }
    }

    socket.onerror = () => {
      if (this.socket !== socket || this.stopped) return
      this.options.onError?.(new Error("Durable turn subscription connection error"))
    }

    socket.onclose = ({ code }) => {
      if (this.socket !== socket) return
      this.socket = null
      if (this.stopped) return
      if (code === 1000) {
        // A clean server-initiated close with no terminal frame (e.g. the
        // backend's `StaleTurnError`/`ChatTurnStorageError` paths in
        // chat_turns_ws.py just return from the generator) is NOT the same
        // as this subscription's own explicit `stop()` — that path nulls
        // `this.socket` before closing, so it never reaches here. Treat it
        // as reconnect-exhausted rather than silently leaving the registry
        // entry stuck non-terminal forever (issue #2513 finding 5).
        this.stopped = true
        this.options.onConnectionStateChange?.("stopped")
        this.notifyReconnectExhausted()
        return
      }
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.stopped = true
      this.options.onError?.(new Error("Durable turn subscription reconnect limit reached"))
      this.options.onConnectionStateChange?.("stopped")
      this.notifyReconnectExhausted()
      return
    }

    const delay = computeBackoffDelayMs(this.reconnectAttempts, {
      base: this.baseReconnectDelayMs,
      max: this.maxReconnectDelayMs,
    })
    this.reconnectAttempts += 1
    this.options.onConnectionStateChange?.("reconnecting")
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect("reconnecting")
    }, delay)
  }

  private notifyReconnectExhausted(): void {
    try {
      const recovery = this.options.onReconnectExhausted?.()
      void Promise.resolve(recovery).catch((error: unknown) => {
        this.options.onError?.(
          error instanceof Error ? error : new Error("Durable turn subscription recovery failed")
        )
      })
    } catch (error) {
      this.options.onError?.(
        error instanceof Error ? error : new Error("Durable turn subscription recovery failed")
      )
    }
  }
}
